import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import {
  AiProductSelectionConditionSignal,
  CampaignIssues,
  CampaignTag,
  DTCampaignVersionType,
  DuplicateNodeResponse,
  FCCampaign,
  RemoteCampaignSignal,
  RemoteCampaignStepsInformation,
  ServerSlimCampaign,
  SlimCampaign,
  SocialCampaignStatuses,
} from './campaigns.types';
import {PostApiOptions, PostDataObj} from '../api/apiTypes';
import {
  toClientCampaignStepsInformation,
  toClientConditions,
  toClientMacroItems,
  toLocalCampaignSignal,
  toLocalSlimCampaign,
  toServerCampaign,
  dtToClientModel,
  dtVersionToClientModel,
  toClientCampaignMiniBrief,
  toServerCampaignMiniBrief,
} from './campaigns.dataConverters';
import apiV2Service from '../api/api-v2.service';
import taxonomyApi from '../taxonomy/taxonomy.api';
import {toLocalSignal} from '../campaignTrackers/campaignTrackers.dataConverters';
import addRandomKeyToObjectArray from 'common/src/utils/arrayUtils/addRandomKeyToObjectArray';
import {
  CategorizedSignal,
  ServerCategorizedSignal,
} from '../compoundConditions/compoundConditions.types';
import {toClientDashboard} from '../dashboards/dashboards.dataConverters';
import {Dashboard, RemoteDashboard} from '../dashboards/dashboards.types';
import {EntityToRename} from 'common/src/modules/shared/copilot/copilot.types';
import {AIConditionSignalWire} from './copilot/agents/campaignSetupAgent/standaloneAgents/promptToCardAgentV2/promptToCardAgentV2.types';
import {toLocalConditionCategories} from '../conditions/conditions.dataConverter';
import {
  ConditionCategory,
  ConditionWizardCategory,
  ServerConditionWizardCategory,
} from '../conditions/conditions.types';
import {MacroItem} from '../macros/macros.types';
import {createPathParamsUrl} from '../api/createPathParamsUrl';
import {
  Nodes,
  ScheduleTiming,
  ServerCampaignVersion,
  ServerCampaignWithDT,
  ServerDecisionTree,
} from '../decisionTree/decisionTree.types';
import enumsService from '../enums/enums.service';
import {CLEnumType} from '../enums/enums.types';
import {PatchTreeParams} from '../campaignVersions/campaignVersions.api';
import {createDBNode} from './utils/createDBNode';
import {
  CampaignBriefResponse,
  CampaignBrief,
  CampaignMiniBrief,
  CampaignMiniBriefResponse,
  RemoteCampaignMiniBriefResponse,
} from './campaignBrief/campaignBrief.types';
import {ServerNode} from '../nodes/nodes.types';

const BASE_PATH = 'Campaign';

async function deleteCampaign(campaignId: number) {
  return apiV2Service.executeDeleteRequest<boolean>(`${BASE_PATH}/${campaignId}`);
}

async function duplicateCampaign(campaignId: number, includeAdGroups = false) {
  return apiV2Service.executePostRequest(`${BASE_PATH}/${campaignId}/Duplicate`, {
    includeAdGroups,
  });
}

interface slimCampaignsRequestFilters {
  type?: string;
  status?: string;
  ids?: number[];
}

async function getSlimCampaigns(
  requestFilters?: slimCampaignsRequestFilters,
): Promise<SlimCampaign[]> {
  const {data} = await apiV2Service.executeGetRequest<ServerSlimCampaign[]>(`${BASE_PATH}/List`, {
    ...(requestFilters?.type && {type: requestFilters.type}),
    ...(requestFilters?.status && {status: requestFilters.status}),
    ...(requestFilters?.ids && {ids: requestFilters.ids}),
  });

  return _map(data, toLocalSlimCampaign);
}

async function add(postDataObj: PostDataObj, postApiOptions: PostApiOptions = {}): Promise<number> {
  return apiV2Service
    .executePutRequest<number>(BASE_PATH, {}, postDataObj, postApiOptions)
    .then((res) => res.data);
}

function getSignals({
  campaignId,
  versionId,
  feedId,
}: {
  campaignId: number;
  versionId: number;
  feedId: number;
}) {
  return apiV2Service
    .executeGetRequest<
      RemoteCampaignSignal[]
    >(`${BASE_PATH}/${campaignId}/version/${versionId}/feed/${feedId}/conditions`)
    .then(({data}) => data.map(toLocalCampaignSignal));
}

function getContentMatchingSignals({
  campaignId,
  versionId,
  previousNodeId,
  previousOutputId,
  existingNodeId,
}: {
  campaignId: number;
  versionId: number;
  existingNodeId?: number;
  previousNodeId: number;
  previousOutputId: number;
}) {
  return apiV2Service
    .executeGetRequest<RemoteCampaignSignal[]>(
      `${BASE_PATH}/${campaignId}/version/${versionId}/matchingStrategySignals`,
      {
        ...(existingNodeId && {existingNodeId}),
        ...(!existingNodeId && !!previousNodeId && {previousNodeId, previousOutputId}),
      },
    )
    .then(({data}) => data.map(toLocalCampaignSignal));
}

async function getFullConditionCategories(
  campaignId: number,
  versionId: number,
): Promise<CategorizedSignal[]> {
  const [taxonomy, serverSignals] = await Promise.all([
    taxonomyApi.getMapping(),
    apiV2Service
      .executeGetRequest<ServerCategorizedSignal[]>(`${BASE_PATH}/GetFullConditionCategories`, {
        campaignId,
        versionId,
      })
      .then(({data}) =>
        data.map((category) => ({...category, categories: category.categoryId ?? []})),
      ),
  ]);

  const signals = serverSignals.map(toLocalSignal);

  return toClientConditions(signals, taxonomy);
}

async function getCampaignStatuses({
  campaignId,
  versionId,
}: {
  campaignId: number;
  versionId: number;
}) {
  const {data} = await apiV2Service.executeGetRequest<SocialCampaignStatuses>(
    `${BASE_PATH}/${campaignId}/version/${versionId}/GetCampaignStatuses`,
  );

  return data;
}

async function deactivateCampaign(campaignId: number) {
  return apiV2Service.executePostRequest<void>(`${BASE_PATH}/${campaignId}/Deactivate`);
}

async function activateCampaign(campaignId: number) {
  return apiV2Service.executePostRequest<void>(`${BASE_PATH}/${campaignId}/Activate`);
}

async function getCampaignIssues(campaignId: number, versionId: number) {
  return apiV2Service
    .executeGetRequest<CampaignIssues>(
      `${BASE_PATH}/${campaignId}/version/${versionId}/GetIssues/issues`,
    )
    .then(({data}) => {
      const {
        campaignLogicIssues,
        thirdPartyTrackersIssues,
        campaignIntegrationIssues,
        placementIssues,
      } = data;
      const nodeIssues = addRandomKeyToObjectArray(campaignLogicIssues);
      const trackersIssues = addRandomKeyToObjectArray(thirdPartyTrackersIssues);
      const integrationIssues = addRandomKeyToObjectArray(campaignIntegrationIssues);
      const placementsIssues = addRandomKeyToObjectArray(placementIssues || []);

      return {
        campaignLogicIssues: nodeIssues.map((issue) => ({...issue, issueKey: issue.key})),
        thirdPartyTrackersIssues: trackersIssues.map((issue) => ({...issue, issueKey: issue.key})),
        campaignIntegrationIssues: integrationIssues.map((issue) => ({
          ...issue,
          issueKey: issue.key,
        })),
        placementIssues: placementsIssues.map((issue) => ({...issue, issueKey: issue.key})),
      };
    });
}

// Campaign Dashboard

async function getCampaignDashboards(campaignId: number): Promise<Dashboard[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteDashboard[]>(
    `${BASE_PATH}/${campaignId}/Dashboards`,
  );

  return _map(data, toClientDashboard);
}

async function getCampaignDefaultDashboard(campaignId: number): Promise<Dashboard> {
  const {data} = await apiV2Service.executeGetRequest<RemoteDashboard>(
    `${BASE_PATH}/${campaignId}/DefaultDashboard`,
  );

  return toClientDashboard(data);
}

async function setAsDefaultCampaignDashboard(
  dashboardId: number,
  campaignId: number,
): Promise<number> {
  const {data} = await apiV2Service.executePutRequest<number>(
    `${BASE_PATH}/${campaignId}/DefaultDashboard/${dashboardId}`,
  );

  return data;
}

async function createCampaignDashboard(campaignId: number): Promise<number> {
  const {data} = await apiV2Service.executePostRequest<number>(
    `${BASE_PATH}/${campaignId}/CreateDashboard`,
  );

  return data;
}

function monitorEmptyIssues(campaignId: number, versionId: number) {
  apiV2Service.executePostRequest<void>(
    `${BASE_PATH}/${campaignId}/version/${versionId}/monitorNoIssuesAfterPatch`,
    {campaignId, versionId},
  );
}

async function removeCampaignDashboard(campaignId: number, dashboardId: number) {
  const {data} = await apiV2Service.executeDeleteRequest<number>(
    `${BASE_PATH}/${campaignId}/${dashboardId}`,
  );

  return data;
}

async function renameCampaigns(campaigns: EntityToRename[]) {
  return Promise.all(
    campaigns.map((campaign) =>
      apiV2Service.executePatchRequest<void>(
        `${BASE_PATH}/${campaign.id}/basicData`,
        {},
        {
          name: campaign.newName,
        },
      ),
    ),
  );
}

async function assignTags(campaignId: number, tags: CampaignTag[]) {
  return apiV2Service.executePatchRequest<void>(
    `${BASE_PATH}/${campaignId}/basicData`,
    {},
    {
      tag1: tags[0],
      tag2: tags[1],
      tag3: tags[2],
    },
  );
}

async function getSignalsByNameForConditionCard(
  signalName: string,
  campaignId: number,
  versionId: number,
): Promise<AIConditionSignalWire[]> {
  const {data} = await apiV2Service.executeGetRequest<AIConditionSignalWire[]>(
    `${BASE_PATH}/${campaignId}/version/${versionId}/searchConditionCardSignals`,
    {
      signalName,
      campaignId,
      versionId,
    },
  );

  return data;
}

async function getSignalsByNameForContentCard({
  signalName,
  campaignId,
  versionId,
}: {
  signalName: string;
  campaignId: number;
  versionId: number;
}): Promise<AiProductSelectionConditionSignal[]> {
  const {data} = await apiV2Service.executeGetRequest<AiProductSelectionConditionSignal[]>(
    `${BASE_PATH}/${campaignId}/version/${versionId}/searchContentMatchingSignals`,
    {
      signalName,
      campaignId,
      versionId,
    },
  );

  return data;
}

async function getSignalsByNameForProductCard({
  signalName,
  versionId,
  productType,
  campaignId,
}: {
  signalName: string;
  versionId: number;
  productType: number;
  campaignId: number;
}): Promise<AiProductSelectionConditionSignal[]> {
  const {data} = await apiV2Service.executeGetRequest<AiProductSelectionConditionSignal[]>(
    `${BASE_PATH}/${campaignId}/version/${versionId}/searchProductMatchingSignals`,
    {
      signalName,
      productType,
    },
  );

  return data;
}

async function fetchCollaborationBadgesData(campaignIds: number[]) {
  const {data} = await apiV2Service.executePostRequest<
    Record<number, RemoteCampaignStepsInformation>
  >(`${BASE_PATH}/CollaborationBadges`, {}, {ids: campaignIds});

  return _mapValues(data, toClientCampaignStepsInformation);
}

async function getSportTeamNameSignals({
  campaignId,
  versionId,
}: {
  campaignId: number;
  versionId: number;
}): Promise<RemoteCampaignSignal[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteCampaignSignal[]>(
    `Signals/${campaignId}/version/${versionId}/sportTeamNameSignals`,
  );

  return data;
}

async function getConditionCategories(campaignId: number): Promise<ConditionWizardCategory[]> {
  const {data: conditionCategories} = await apiV2Service.executeGetRequest<
    ServerConditionWizardCategory[]
  >(`${BASE_PATH}/GetConditionCategories`, {campaignId});

  return toLocalConditionCategories(conditionCategories);
}

async function getConditionOptions({
  campaignId,
  versionId,
  categories,
}: {
  campaignId: number;
  versionId: number;
  categories: ConditionCategory[];
}): Promise<MacroItem[]> {
  const [conditions, taxonomy] = await Promise.all([
    apiV2Service
      .executePostRequest<
        MacroItem[]
      >(`${BASE_PATH}/${campaignId}/version/${versionId}/conditions`, {}, {categories})
      .then((res) => res.data),
    taxonomyApi.getMapping(),
  ]);

  return toClientMacroItems(conditions, taxonomy, categories[0]);
}

async function getDTCampaign(campaignId: number, versionNumber: string | DTCampaignVersionType) {
  const baseUrl = `${BASE_PATH}/${campaignId}/version`;
  let url: string;

  switch (versionNumber) {
    case DTCampaignVersionType.Latest:
      url = createPathParamsUrl(`${baseUrl}/latest`, {
        campaignId,
      });
      break;
    case DTCampaignVersionType.Draft:
      url = createPathParamsUrl(`${baseUrl}/draft`, {
        campaignId,
      });
      break;
    default:
      url = createPathParamsUrl(`${baseUrl}/number/:versionNumber`, {
        campaignId,
        versionNumber,
      });
      break;
  }

  const [generalEnums, campaignEnums, relatedEnums, {data}] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    enumsService.loadEnums(CLEnumType.RelatedItems),
    apiV2Service.executeGetRequest<ServerCampaignWithDT>(url, {}),
  ]);

  return dtToClientModel({
    decisionTree: data.loadedVersion.decisionTree,
    version: data.loadedVersion,
    campaignEnums,
    generalEnums,
    relatedEnums,
  });
}

function updateVersionMetadata({
  campaignId,
  versionId,
  name,
  description,
}: {
  campaignId: number;
  versionId: number;
  description: string;
  name: string;
}) {
  return apiV2Service.executePutRequest(
    `${BASE_PATH}/${campaignId}/version/${versionId}/metadata`,
    {},
    {name, description},
    {sendAsFormData: false},
  );
}

async function scheduleVersionActivation(
  campaignId: number,
  versionNumber: number,
  cronExpression: string,
  timeZone: string,
  activationOption: number,
) {
  return apiV2Service.executePostRequest(
    `${BASE_PATH}/${campaignId}/version/number/${versionNumber}/scheduledActivation`,
    {},
    {
      cronExpression,
      timeZone,
      ...(activationOption !== undefined ? {activation: {activationOption}} : {}),
    },
  );
}

async function cancelVersionScheduledActivation(campaignId: number, versionNumber: number) {
  return apiV2Service.executeDeleteRequest(
    `${BASE_PATH}/${campaignId}/version/number/${versionNumber}/scheduledActivation`,
  );
}

async function getCampaignRenderingSchedule(campaignId: number) {
  const {data} = await apiV2Service.executeGetRequest<ScheduleTiming>(
    `${BASE_PATH}/${campaignId}/ActiveVersion/AdServingAssets/UpdateSchedule`,
  );

  return data;
}

async function setCampaignRenderingSchedule(
  campaignId: number,
  cronExpression: string,
  timeZone: string,
) {
  return apiV2Service.executePostRequest(
    `${BASE_PATH}/${campaignId}/ActiveVersion/AdServingAssets/UpdateSchedule`,
    {},
    {cronExpression, timeZone},
    {sendAsFormData: false},
  );
}

async function disableCampaignRenderingSchedule(campaignId: number) {
  return apiV2Service.executeDeleteRequest(
    `${BASE_PATH}/${campaignId}/ActiveVersion/AdServingAssets/UpdateSchedule`,
  );
}

async function saveCampaignVersion({
  description,
  name,
  campaignId,
  versionId,
}: {
  campaignId: number;
  name: string;
  versionId: number;
  description: string;
}) {
  return apiV2Service.executePostRequest<ServerCampaignWithDT>(
    `${BASE_PATH}/${campaignId}/version/${versionId}/save`,
    {},
    {name, description},
    {sendAsFormData: false},
  );
}

async function getCampaignVersionsList(campaignId: number) {
  const {data: versions} = await apiV2Service.executeGetRequest<ServerCampaignVersion[]>(
    `${BASE_PATH}/${campaignId}/GetVersionsMetadata`,
  );

  return _map(versions, dtVersionToClientModel);
}

async function updateTree({
  campaignId,
  versionNumber,
  nodesToAdd,
  nodesToUpdate,
  nodesToDelete,
  campaignNode,
  rootNodeId,
  fallbackCreativeNodeId,
}: PatchTreeParams) {
  const serverNodesToAdd = _map(nodesToAdd, createDBNode);
  const serverNodesToUpdate = _map(nodesToUpdate, createDBNode);
  const serverNodesToDelete = _map(nodesToDelete, createDBNode);

  const formObj = {
    deletedNodes: serverNodesToDelete,
    newNodes: serverNodesToAdd,
    updatedNodes: serverNodesToUpdate,
    ...(campaignNode && {campaignNode: {campaign: toServerCampaign(campaignNode)}}),
    rootNodeId,
    fallbackCreativeNodeId,
  };

  const [generalEnums, campaignEnums, relatedEnums, {data}] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    enumsService.loadEnums(CLEnumType.RelatedItems),
    apiV2Service.executePatchRequest<ServerCampaignWithDT>(
      `${BASE_PATH}/${campaignId}/version/number/${versionNumber}/tree`,
      {},
      formObj,
      {
        sendAsFormData: false,
      },
    ),
  ]);

  return dtToClientModel({
    decisionTree: data.loadedVersion.decisionTree,
    version: data.loadedVersion,
    campaignEnums,
    generalEnums,
    relatedEnums,
  });
}

async function isRendering(campaignId: number) {
  const {data} = await apiV2Service.executeGetRequest<boolean>(
    `${BASE_PATH}/${campaignId}/IsRendering`,
  );

  return data;
}

function getBrief(campaignId: number, versionId: number): Promise<CampaignBriefResponse> {
  return apiV2Service
    .executeGetRequest<CampaignBriefResponse>(
      `${BASE_PATH}/${campaignId}/version/${versionId}/brief`,
    )
    .then((res) => res.data);
}

function updateBrief(campaignId: number, versionId: number, brief: CampaignBrief) {
  return apiV2Service.executePutRequest<void>(
    `${BASE_PATH}/${campaignId}/version/${versionId}/brief`,
    {},
    {brief},
  );
}

async function getConcept(campaignId: number, versionId: number) {
  const [generalEnums, campaignEnums, relatedEnums, {data}] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    enumsService.loadEnums(CLEnumType.RelatedItems),
    apiV2Service.executeGetRequest<ServerDecisionTree>(
      `${BASE_PATH}/${campaignId}/version/${versionId}/conceptplan`,
      {},
    ),
  ]);

  return dtToClientModel({
    decisionTree: data,
    campaignEnums,
    generalEnums,
    relatedEnums,
  });
}

async function updateConcept({
  campaignId,
  versionId,
  nodesToAdd,
  nodesToUpdate,
  nodesToDelete,
  campaignNode,
  rootNodeId,
  fallbackCreativeNodeId,
}: {
  campaignId: number;
  versionId: number;
  nodesToDelete?: Nodes;
  nodesToUpdate?: Nodes;
  nodesToAdd?: Nodes;
  campaignNode?: FCCampaign;
  rootNodeId?: number;
  fallbackCreativeNodeId?: number;
}) {
  const serverNodesToAdd = _map(nodesToAdd, createDBNode);
  const serverNodesToUpdate = _map(nodesToUpdate, createDBNode);
  const serverNodesToDelete = _map(nodesToDelete, createDBNode);

  const formObj = {
    deletedNodes: serverNodesToDelete,
    newNodes: serverNodesToAdd,
    updatedNodes: serverNodesToUpdate,
    ...(campaignNode && {campaignNode: {campaign: toServerCampaign(campaignNode)}}),
    rootNodeId,
    fallbackCreativeNodeId,
  };

  const [generalEnums, campaignEnums, relatedEnums, {data}] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    enumsService.loadEnums(CLEnumType.RelatedItems),
    apiV2Service.executePatchRequest<ServerDecisionTree>(
      `${BASE_PATH}/${campaignId}/version/${versionId}/patchConceptPlan`,
      {},
      formObj,
      {
        sendAsFormData: false,
      },
    ),
  ]);

  return dtToClientModel({
    decisionTree: data,
    campaignEnums,
    generalEnums,
    relatedEnums,
  });
}

async function importFromConceptPlan({
  campaignId,
  versionId,
}: {
  campaignId: number;
  versionId: number;
}) {
  const [generalEnums, campaignEnums, relatedEnums, {data}] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    enumsService.loadEnums(CLEnumType.RelatedItems),
    apiV2Service.executePostRequest<ServerDecisionTree>(
      `${BASE_PATH}/${campaignId}/version/${versionId}/GenerateDraftFromConceptPlan`,
      {},
      {},
      {
        sendAsFormData: false,
      },
    ),
  ]);

  return dtToClientModel({
    decisionTree: data,
    campaignEnums,
    generalEnums,
    relatedEnums,
  });
}

async function validateNodesBeforeDuplication({
  fromCampaignId,
  toCampaignId,
  nodes,
}: {
  fromCampaignId: number;
  toCampaignId: number;
  nodes: ServerNode[];
}): Promise<{invalidNodes: DuplicateNodeResponse[]}> {
  return apiV2Service
    .executePostRequest<{
      invalidNodes: DuplicateNodeResponse[];
    }>(
      `${BASE_PATH}/ValidateNodesBeforeDuplication/from/${fromCampaignId}/to/${toCampaignId}`,
      {},
      {
        nodes,
      },
    )
    .then((res) => res.data);
}

function getMiniBrief(campaignId: number, versionId: number): Promise<CampaignMiniBriefResponse> {
  return apiV2Service
    .executeGetRequest<RemoteCampaignMiniBriefResponse>(
      `${BASE_PATH}/${campaignId}/version/${versionId}/miniBrief`,
    )
    .then(({data}) => ({
      ...data,
      brief: toClientCampaignMiniBrief(data.brief),
    }));
}

function updateMiniBrief({
  campaignId,
  versionNumber,
  brief,
}: {
  campaignId: number;
  versionNumber: number;
  brief: CampaignMiniBrief;
}) {
  return apiV2Service.executePutRequest<void>(
    `${BASE_PATH}/${campaignId}/version/${versionNumber}/miniBrief`,
    {},
    {
      ...toServerCampaignMiniBrief(brief),
    },
  );
}

const campaignsApi = {
  add,
  deleteCampaign,
  duplicateCampaign,
  getSlimCampaigns,
  getSignals,
  getContentMatchingSignals,
  getFullConditionCategories,
  activateCampaign,
  deactivateCampaign,
  getCampaignStatuses,
  getCampaignIssues,
  // Campaign Dashboard
  getCampaignDefaultDashboard,
  getCampaignDashboards,
  setAsDefaultCampaignDashboard,
  createCampaignDashboard,
  monitorEmptyIssues,
  removeCampaignDashboard,
  renameCampaigns,
  assignTags,
  getSignalsByNameForConditionCard,
  getSignalsByNameForContentCard,
  getSignalsByNameForProductCard,
  fetchCollaborationBadgesData,
  getSportTeamNameSignals,
  getConditionCategories,
  getConditionOptions,
  getDTCampaign,
  updateVersionMetadata,
  scheduleVersionActivation,
  cancelVersionScheduledActivation,
  getCampaignRenderingSchedule,
  setCampaignRenderingSchedule,
  disableCampaignRenderingSchedule,
  saveCampaignVersion,
  getCampaignVersionsList,
  updateTree,
  isRendering,
  getBrief,
  updateBrief,
  getConcept,
  updateConcept,
  importFromConceptPlan,
  validateNodesBeforeDuplication,
  getMiniBrief,
  updateMiniBrief,
};

export default campaignsApi;
